import '@fontsource/cormorant-garamond/300.css';
import '@fontsource/cormorant-garamond/400.css';
import '@fontsource/cormorant-garamond/400-italic.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import { lazy, Suspense } from 'react';
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Index from "./pages/Index";

// Lazy load non-critical UI components to reduce initial bundle
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const NewsletterPopup = lazy(() => import("./components/newsletter/NewsletterPopup").then(m => ({ default: m.NewsletterPopup })));
const WhatsAppButton = lazy(() => import("./components/social-proof/WhatsAppButton").then(m => ({ default: m.WhatsAppButton })));

// Lazy load non-critical routes
const ShopProductPage = lazy(() => import("./pages/ShopProductPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const SizeGuidePage = lazy(() => import("./pages/SizeGuidePage"));
const TransparencyPage = lazy(() => import("./pages/TransparencyPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ShippingPage = lazy(() => import("./pages/ShippingPage"));
const RefundsPage = lazy(() => import("./pages/RefundsPage"));
const TrackingPage = lazy(() => import("./pages/TrackingPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogArticlePage = lazy(() => import("./pages/BlogArticlePage"));
const CollectionsPage = lazy(() => import("./pages/CollectionsPage"));
const CollectionPage = lazy(() => import("./pages/CollectionPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const ReviewsPage = lazy(() => import("./pages/ReviewsPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Suspense fallback={null}>
          <Sonner />
          <NewsletterPopup />
          <WhatsAppButton />
        </Suspense>
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/shop/:handle" element={<ShopProductPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/produits" element={<ProductsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/a-propos" element={<AboutPage />} />
              <Route path="/transparency" element={<TransparencyPage />} />
              <Route path="/transparence" element={<TransparencyPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/shipping" element={<ShippingPage />} />
              <Route path="/livraison" element={<ShippingPage />} />
              <Route path="/returns" element={<RefundsPage />} />
              <Route path="/retours" element={<RefundsPage />} />
              <Route path="/tracking" element={<TrackingPage />} />
              <Route path="/suivi" element={<TrackingPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/cgv" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/confidentialite" element={<PrivacyPage />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogArticlePage />} />
              <Route path="/collections" element={<CollectionsPage />} />
              <Route path="/collections/:slug" element={<CollectionPage />} />
              <Route path="/size-guide" element={<SizeGuidePage />} />
              <Route path="/guide-tailles" element={<SizeGuidePage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/compare" element={<ComparePage />} />
              <Route path="/comparatif" element={<ComparePage />} />
              <Route path="/reviews" element={<ReviewsPage />} />
              <Route path="/avis" element={<ReviewsPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/compte" element={<AccountPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
